import { useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { AlertTriangle, CheckCircle2, Loader2, Mail } from "lucide-react"

import {
  BrandResponse,
  DcFillRate,
  InboundAlert,
  InboundFillRateResponse,
  fetchInboundFillRate,
  updateInboundPoLine,
} from "@/api/inbound_po_line.tsx"
import { UserState } from "@/auth/user"
import { ShortLineAnswer } from "@/components/ShortLineAnswer.tsx"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { toast } from "@/components/ui/use-toast"
import {
  AlertFilter,
  chart_rows,
  filter_alerts,
  format_rate,
  receiving_dispute_mailto,
  total_shorted_value,
} from "@/fill_rate/fill_rate_display.ts"
import { exactCurrencyFormatter, usePageTitle } from "@/utils"

const WEEKS = 26

const FILTERS: { key: AlertFilter; label: string }[] = [
  { key: "unanswered", label: "Needs an answer" },
  { key: "shortages", label: "Short shipments" },
  { key: "short_dated", label: "Short-dated" },
  { key: "all", label: "All" },
]

function DcCard({ dc, threshold, className }: { dc: DcFillRate; threshold: number; className?: string }) {
  const rows = chart_rows(dc)
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div class="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">UNFI {dc.label}</CardTitle>
            <CardDescription>Rolling 13-week inbound fill rate</CardDescription>
          </div>
          <div class="text-right">
            <div class={`text-3xl font-semibold ${dc.below_threshold ? "text-red-600" : "text-emerald-700"}`}>
              {format_rate(dc.current_rolling_fill_rate)}
            </div>
            <Badge variant={dc.below_threshold ? "rose" : "emerald"}>
              {dc.below_threshold ? `Below ${format_rate(threshold)} — fines apply` : `At or above ${format_rate(threshold)}`}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div class="text-sm text-slate-600 mb-3">
          {dc.short_cases_in_window.toLocaleString()} cases short in the window
          {dc.shorted_value_in_window > 0 && <> · {exactCurrencyFormatter(dc.shorted_value_in_window)} at UNFI cost</>}
        </div>
        <div class="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" fontSize={11} interval="preserveStartEnd" />
              <YAxis domain={[(data_min: number) => Math.min(60, Math.floor(data_min / 10) * 10), 100]} tickFormatter={(v: number) => `${v}%`} width={44} fontSize={11} />
              <Tooltip formatter={(value: number) => [`${value}%`, "Rolling fill rate"]} />
              <ReferenceLine y={threshold * 100} stroke="#dc2626" strokeDasharray="4 4" label={{ value: "98%", fontSize: 11, fill: "#dc2626", position: "insideTopRight" }} />
              <Line type="monotone" dataKey="rolling" stroke="#2e2ef8" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

function ResponseCell({ alert, saving, onAnswer }: { alert: InboundAlert; saving: boolean; onAnswer: (response: BrandResponse | null) => void }) {
  if (!alert.is_shortage) {
    return <span class="text-slate-400 text-sm">—</span>
  }
  return <ShortLineAnswer brand_response={alert.brand_response} saving={saving} onAnswer={onAnswer} />
}

function AlertsTable({ alerts, vendor_label, onAnswered }: { alerts: InboundAlert[]; vendor_label: string; onAnswered: () => void }) {
  const [saving_id, setSavingId] = useState<string | null>(null)

  async function answer(alert: InboundAlert, response: BrandResponse | null) {
    setSavingId(alert.id)
    const res = await updateInboundPoLine(alert.id, response)
    setSavingId(null)
    if (!res.ok) {
      toast({ title: "Could not save the answer", description: res.error.message, variant: "destructive" })
      return
    }
    onAnswered()
    const mailto = response === "shipped_in_full" ? receiving_dispute_mailto(alert, vendor_label) : null
    if (mailto) {
      window.location.href = mailto
    }
  }

  if (alerts.length === 0) {
    return <div class="text-sm text-slate-500 py-6 text-center">Nothing here.</div>
  }
  return (
    <div class="overflow-x-auto">
      <table class="w-full min-w-[960px] text-sm">
        <thead>
          <tr class="text-left text-slate-500 border-b">
            <th class="py-2 pr-4 font-medium">Received</th>
            <th class="py-2 pr-4 font-medium">DC</th>
            <th class="py-2 pr-4 font-medium">PO</th>
            <th class="py-2 pr-4 font-medium">Item</th>
            <th class="py-2 pr-4 font-medium text-right">Ordered</th>
            <th class="py-2 pr-4 font-medium text-right">Received</th>
            <th class="py-2 pr-4 font-medium text-right">Short value</th>
            <th class="py-2 pr-4 font-medium">Short-dated</th>
            <th class="py-2 font-medium">Your answer</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((a: InboundAlert) => {
            const mailto = receiving_dispute_mailto(a, vendor_label)
            return (
              <tr key={a.id} class="border-b last:border-0 align-top">
                <td class="py-2 pr-4 whitespace-nowrap">{a.received_date ? format(parseISO(a.received_date), "MMM d, yyyy") : "—"}</td>
                <td class="py-2 pr-4 whitespace-nowrap">{a.dc_label}</td>
                <td class="py-2 pr-4 font-mono">{a.po_number}</td>
                <td class="py-2 pr-4">
                  <div class="font-mono">{a.item_number}{a.brand_sku ? ` · ${a.brand_sku}` : ""}</div>
                  <div class="text-slate-500">{a.description}</div>
                </td>
                <td class="py-2 pr-4 text-right">{a.quantity_ordered ?? "—"}</td>
                <td class={`py-2 pr-4 text-right ${a.quantity_short > 0 ? "text-red-600 font-medium" : ""}`}>{a.quantity_received ?? "—"}</td>
                <td class="py-2 pr-4 text-right">{a.shorted_value ? exactCurrencyFormatter(a.shorted_value) : "—"}</td>
                <td class="py-2 pr-4">
                  {a.is_short_dated ? (
                    <span>
                      {a.short_dated_cases} cs, {a.days_short_of_guarantee} days short
                      {a.expiry_date && <div class="text-slate-500">exp. {format(parseISO(a.expiry_date), "MMM d, yyyy")}</div>}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td class="py-2">
                  <ResponseCell alert={a} saving={saving_id === a.id} onAnswer={(r: BrandResponse | null) => answer(a, r)} />
                  {a.brand_response === "shipped_in_full" && mailto && (
                    <a href={mailto} class="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                      <Mail className="h-3 w-3" /> Email {a.inventory_control_email}
                    </a>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Fill rate: the brand's rolling 13-week inbound fill rate at each UNFI Canada DC, rebuilt from
 * UNFI's receiving alerts and purchase summaries, and the alerts that need an answer. Loblaw fines
 * UNFI below 98%, and UNFI passes those fines to brands whose inbound shipments were short.
 */
export function FillRatePage() {
  usePageTitle("Fill rate")
  const signer = UserState.use((u) => [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || "")
  const [filter, setFilter] = useState<AlertFilter>("unanswered")
  const { data, error, isLoading, mutate } = useSWR<InboundFillRateResponse>(["inbound-fill-rate", WEEKS], () => fetchInboundFillRate(WEEKS), {
    revalidateOnFocus: false,
  })

  if (isLoading) {
    return (
      <div class="flex items-center gap-2 p-8 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading fill rate…
      </div>
    )
  }
  if (error || !data) {
    return <div class="p-8 text-red-600">Could not load fill rate.</div>
  }

  const visible = filter_alerts(data.alerts, filter)
  const unanswered = filter_alerts(data.alerts, "unanswered")
  return (
    <div class="p-6 flex flex-col gap-6 max-w-7xl">
      <div>
        <h1 class="text-2xl font-semibold">Inbound fill rate</h1>
        <p class="text-slate-600 mt-1 max-w-3xl">
          Loblaw fines UNFI when its deliveries fall below 98% over 13 weeks, and UNFI passes those fines to brands whose shipments
          into UNFI were short. This is your rolling rate at each UNFI DC, built from UNFI's own receiving reports.
        </p>
      </div>

      {data.dcs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-slate-500">No UNFI Canada receiving data yet.</CardContent>
        </Card>
      ) : (
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.dcs.map((dc: DcFillRate) => (
            <DcCard key={dc.dc ?? "unknown"} dc={dc} threshold={data.threshold} />
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <div class="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {unanswered.length > 0 ? <AlertTriangle className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                UNFI receiving alerts
              </CardTitle>
              <CardDescription>
                {unanswered.length} short shipment{unanswered.length === 1 ? "" : "s"} need an answer
                {unanswered.length > 0 && <> · {exactCurrencyFormatter(total_shorted_value(unanswered))} at UNFI cost</>}. If you shipped in
                full, tell UNFI's inventory desk now — a receiving error left standing becomes a debit note and counts toward a fill-rate fine.
              </CardDescription>
            </div>
            <div class="flex gap-1">
              {FILTERS.map((f) => (
                <Button key={f.key} size="sm" variant={filter === f.key ? "default" : "ghost"} onClick={() => setFilter(f.key)}>
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <AlertsTable alerts={visible} vendor_label={signer} onAnswered={() => mutate()} />
        </CardContent>
      </Card>
    </div>
  )
}
